import get from 'lodash/get'
import React, {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
} from 'react'
import useSWR, { useSWRConfig } from 'swr'

import { MetadataContext } from '@Globals/Metadata'

interface ResponseData {
  status: 'OK' | 'ERROR'
}

interface UserDataOtherCountryRaw {
  country: string
  countryName: Record<number, string>
  isEuCountry: boolean
}

interface UserDataWishlistRaw {
  abundance: number
  productIds: number[]
}

interface UserDataRaw {
  basketQty: number
  geoIpCountryCode?: string
  loggedIn: boolean
  otherCountryData?: UserDataOtherCountryRaw
  quoteId: string
  wishlist: UserDataWishlistRaw
}

export interface Basket {
  quantity: number
}

interface OtherCountry {
  country: string
  countryNameMap: Record<number, string>
  isEU: boolean
}

export interface WishList {
  abundance: number
  productIDs: number[]
}

export interface UserData {
  basket: Basket
  geoIPCountryCode?: string
  loggedIn: boolean
  otherCountry?: OtherCountry
  quoteID: string
  wishList: WishList
}

interface UserProviderProps {
  children: ReactNode
}

interface UserProviderValue {
  error?: string
  loading: boolean
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<boolean>
  resetPassword: (email: string) => Promise<boolean>
  revalidate: () => Promise<boolean>
  userData?: UserData
}

const convertRaw = (input?: UserDataRaw): UserData | null => {
  if (!input) {
    return null
  }

  let otherCountry: OtherCountry | undefined

  if (input.otherCountryData) {
    otherCountry = {
      country: input.otherCountryData.country,
      countryNameMap: input.otherCountryData.countryName,
      isEU: input.otherCountryData.isEuCountry,
    }
  }

  return {
    basket: {
      quantity: input.basketQty,
    },
    geoIPCountryCode: input.geoIpCountryCode,
    loggedIn: input.loggedIn,
    otherCountry,
    quoteID: input.quoteId,
    wishList: {
      abundance: input.wishlist.abundance,
      productIDs: input.wishlist.productIds,
    },
  }
}

const fetcher = async (input: RequestInfo, init?: RequestInit): Promise<UserData | null> => {
  const res = await fetch(input, init)

  const json = await res.json()

  return convertRaw(get(json, 'data'))
}

export const UserContext = createContext<UserProviderValue>({} as UserProviderValue)
export const UserConsumer = UserContext.Consumer

const ClientUserProvider: FC<UserProviderProps> = ({ children }: UserProviderProps) => {
  const { storeURL } = useContext(MetadataContext)
  const userDataURL = storeURL('scripts/development/endpoints/userData.php', undefined, true)
  const loginReqURL = storeURL('customer/account/loginPost')
  const resetPasswordURL = storeURL('customer/account/forgotPasswordPost')
  const logoutURL = storeURL('customer/account/logout')

  const { mutate } = useSWRConfig()
  const { data, error } = useSWR(userDataURL, fetcher)

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const res = await fetch(loginReqURL, {
          body: JSON.stringify({
            email,
            password,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        })

        const json: ResponseData = await res.json()

        if (json.status !== 'OK') {
          return false
        }
      } catch (err) {
        return false
      }

      try {
        await mutate(userDataURL)
      } catch (err) {
        // ignore errors if we fail to revalidate
      }

      return true
    },
    [loginReqURL, mutate, userDataURL],
  )

  const resetPassword = useCallback(async (email: string) => {
    try {
      const res = await fetch(resetPasswordURL, {
        body: JSON.stringify({ email }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      })

      const json = await res.json()

      if (json.status !== 'OK') {
        return false
      }
    } catch (err) {
      return false
    }

    return true
  }, [resetPasswordURL])

  const logout = useCallback(
    async () => {
      try {
        await fetch(logoutURL, { method: 'POST' })

        try {
          await mutate(userDataURL)
        } catch (err) {
          // ignore errors if we fail to revalidate
        }

        return true
      } catch (err) {
        return false
      }
    },
    [logoutURL, mutate, userDataURL],
  )

  const revalidate = useCallback(() => mutate(userDataURL), [mutate, userDataURL])

  return (
    <UserContext.Provider
      // TODO: avoid rerendering when no user data changes
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        error,
        loading: !data,
        login,
        logout,
        resetPassword,
        revalidate,
        userData: data || undefined,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

const ServerUserProvider: FC<UserProviderProps> = ({ children }: UserProviderProps) => (
  // eslint-disable-next-line react/jsx-no-constructed-context-values
  <UserContext.Provider value={{
    error: undefined,
    loading: true,
    login: async () => false,
    logout: async () => false,
    resetPassword: async () => false,
    revalidate: async () => false,
    userData: undefined,
  }}
  >
    {children}
  </UserContext.Provider>
)

export const UserProvider: FC<UserProviderProps> = (
  {
    children,
  }: UserProviderProps,
) => {
  if (typeof window === 'undefined') {
    return (
      <ServerUserProvider>{children}</ServerUserProvider>
    )
  }

  return <ClientUserProvider>{children}</ClientUserProvider>
}

